/**
 * Computes the four backend notification flags by diffing two ChangeSnapshots.
 *
 * Shared invite fields — update_calendar_invite to BOTH candidate + interviewer:
 *   interviewName, interview_type, duration, start_time, end_time,
 *   location, calendar_visibility, enable_note_taker, host_id, meeting_room_ids
 *
 * Interviewer-only field — update_calendar_invite_to_interviewer + resend_email_to_interviewer:
 *   participant_ids  (also triggers candidate notifications via VARIABLE_BACKED_FIELDS
 *                    when an interviewer variable appears in the candidate's content)
 *
 * Calendar body edits → update_calendar_invite_to_*
 * Email comm config edits → resend_email_to_*
 * Variable-backed fields → whichever flag matches the body where the variable appears
 */
import type { InterviewUpdateFlags } from '../config/types';
import type { ChangeSnapshot } from './buildChangeSnapshot';
import { snapshotFieldChanged } from './buildChangeSnapshot';

export type SaveConfirmationVariant =
  | 'invites'
  | 'emails'
  | 'invites-and-emails'
  | null;

// Details fields that update calendar invites for BOTH candidate and interviewer.
const SHARED_INVITE_FIELDS: (keyof ChangeSnapshot)[] = [
  'interviewName',
  'interview_type',
  'duration',
  'start_time',
  'end_time',
  'location',
  'calendar_visibility',
  'enable_note_taker',
  'host_id',
  'meeting_room_ids',
];

// Participant change updates interviewer invite + interviewer email.
// Candidate is affected only when an interviewer variable appears in their content
// (handled via VARIABLE_BACKED_FIELDS below).
const INTERVIEWER_ONLY_FIELDS: (keyof ChangeSnapshot)[] = ['participant_ids'];

/**
 * Covers gaps not handled by SHARED_INVITE_FIELDS / INTERVIEWER_ONLY_FIELDS:
 *
 * - SHARED_INVITE_FIELDS already sets invite flags for these fields.
 *   Entries here add EMAIL resend coverage when the variable appears in an email body/subject.
 *
 * - INTERVIEWER_ONLY_FIELDS already covers the interviewer side for participant_ids.
 *   The entry here adds CANDIDATE coverage when an interviewer variable appears in candidate content.
 *
 * - candidate_phone is not in any direct list — fully covered here.
 *
 * When a backing field changes, we scan the ORIGINAL sent content for those variables.
 * If found, the corresponding flag is set even though the body text itself didn't change.
 *
 * To add a new business rule: append an entry below.
 */
const VARIABLE_BACKED_FIELDS: Array<{
  snapshotFields: (keyof ChangeSnapshot)[];
  variables: string[];
}> = [
  {
    snapshotFields: ['interviewName'],
    variables: ['{{interview.name}}'],
  },
  {
    // Only the onsite physical address is location-dependent.
    // `{{interview.link}}` is the candidate profile/scorecard link
    // (location-independent, present in nearly every interviewer-notice
    // template) — including it here falsely flagged an interviewer/
    // candidate email resend on every location change. The meeting/join
    // link is not a body variable; it lives in the calendar invite,
    // which already updates on location via SHARED_INVITE_FIELDS.
    snapshotFields: ['location'],
    variables: ['{{interview.onsite_location}}'],
  },
  {
    snapshotFields: ['start_time', 'end_time'],
    variables: ['{{interview.time}}'],
  },
  {
    snapshotFields: ['duration'],
    variables: ['{{interview.duration}}'],
  },
  {
    // Any interviewer variable — single or all — is backed by the participants list
    snapshotFields: ['participant_ids'],
    variables: [
      '{{interview.interviewer_first_name}}',
      '{{interview.interviewer_last_name}}',
      '{{interview.interviewer_full_name}}',
      '{{interview.interviewer_email}}',
      '{{interview.interviewer_title}}',
      '{{interview.all_interviewers_first_names}}',
      '{{interview.all_interviewers_last_names}}',
      '{{interview.all_interviewers_full_names}}',
      '{{interview.all_interviewers_emails}}',
      '{{interview.all_interviewers_titles}}',
    ],
  },
  {
    snapshotFields: ['candidate_phone'],
    variables: ['{{candidate.phone_number}}'],
  },
];

export type UpdateFlags = InterviewUpdateFlags;

export function computeUpdateFlags(
  original: ChangeSnapshot,
  current: ChangeSnapshot
): { flags: UpdateFlags; variant: SaveConfirmationVariant } {
  const sharedInviteChanged = SHARED_INVITE_FIELDS.some((f) =>
    snapshotFieldChanged(original, current, f)
  );
  const interviewerOnlyChanged = INTERVIEWER_ONLY_FIELDS.some((f) =>
    snapshotFieldChanged(original, current, f)
  );

  const candidateCalendarChanged = snapshotFieldChanged(
    original,
    current,
    'candidate_calendar_body'
  );
  const interviewerCalendarChanged = snapshotFieldChanged(
    original,
    current,
    'interviewer_calendar_body'
  );
  // For `from`, only flag as changed when the original had an explicit sender.
  // When original.from is null (API didn't return from_user_id), the form
  // auto-seeds from = <current user> on mount — that's not a user action.
  const candidateFromChanged =
    original.candidate_email_from !== null &&
    snapshotFieldChanged(original, current, 'candidate_email_from');
  const interviewerFromChanged =
    original.interviewer_email_from !== null &&
    snapshotFieldChanged(original, current, 'interviewer_email_from');

  const candidateEmailBodyChanged =
    snapshotFieldChanged(original, current, 'candidate_email_enabled') ||
    snapshotFieldChanged(original, current, 'candidate_ics_enabled') ||
    candidateFromChanged ||
    snapshotFieldChanged(original, current, 'candidate_email_cc') ||
    snapshotFieldChanged(original, current, 'candidate_email_bcc') ||
    snapshotFieldChanged(original, current, 'candidate_email_subject') ||
    snapshotFieldChanged(original, current, 'candidate_email_body') ||
    snapshotFieldChanged(original, current, 'candidate_email_attachments');
  const interviewerEmailBodyChanged =
    snapshotFieldChanged(original, current, 'interviewer_email_enabled') ||
    snapshotFieldChanged(original, current, 'interviewer_ics_enabled') ||
    interviewerFromChanged ||
    snapshotFieldChanged(original, current, 'interviewer_email_cc') ||
    snapshotFieldChanged(original, current, 'interviewer_email_bcc') ||
    snapshotFieldChanged(original, current, 'interviewer_email_subject') ||
    snapshotFieldChanged(original, current, 'interviewer_email_body') ||
    snapshotFieldChanged(original, current, 'interviewer_email_attachments');

  let variableBackedCandidateEmailChanged = false;
  let variableBackedInterviewerEmailChanged = false;
  let variableBackedCandidateInviteChanged = false;
  let variableBackedInterviewerInviteChanged = false;
  // We check against the ORIGINAL bodies — those are what was already sent.
  // If the variable appeared there and its backing data changed, resend/update.
  const candEmailContent =
    String(original.candidate_email_body ?? '') +
    ' ' +
    String(original.candidate_email_subject ?? '');
  const intEmailContent =
    String(original.interviewer_email_body ?? '') +
    ' ' +
    String(original.interviewer_email_subject ?? '');
  const candCalContent = String(original.candidate_calendar_body ?? '');
  const intCalContent = String(original.interviewer_calendar_body ?? '');
  for (const { snapshotFields, variables } of VARIABLE_BACKED_FIELDS) {
    let fieldChanged = snapshotFields.some((f) =>
      snapshotFieldChanged(original, current, f)
    );
    // candidate_phone is a UI-only field that hydrates lazily from
    // applicationData.contact, so a snapshot diff alone is not a real
    // edit. Mirror useScheduleSave's gate: only count as edited when
    // the user has actually opened the inline phone-edit pencil.
    if (
      fieldChanged &&
      snapshotFields.length === 1 &&
      snapshotFields[0] === 'candidate_phone' &&
      current.isCandidatePhoneEditing !== 'edit'
    ) {
      fieldChanged = false;
    }
    if (!fieldChanged) continue;
    const inAny = (content: string) =>
      variables.some((v) => content.includes(v));
    if (inAny(candEmailContent)) variableBackedCandidateEmailChanged = true;
    if (inAny(intEmailContent)) variableBackedInterviewerEmailChanged = true;
    if (inAny(candCalContent)) variableBackedCandidateInviteChanged = true;
    if (inAny(intCalContent)) variableBackedInterviewerInviteChanged = true;
  }

  const flags: UpdateFlags = {
    // Calendar invite body or structural invite fields → resend iCal
    update_calendar_invite_to_candidate:
      sharedInviteChanged ||
      candidateCalendarChanged ||
      variableBackedCandidateInviteChanged,
    update_calendar_invite_to_interviewer:
      sharedInviteChanged ||
      interviewerOnlyChanged ||
      interviewerCalendarChanged ||
      variableBackedInterviewerInviteChanged,
    resend_email_to_candidate:
      candidateEmailBodyChanged || variableBackedCandidateEmailChanged,
    resend_email_to_interviewer:
      interviewerEmailBodyChanged ||
      interviewerOnlyChanged ||
      variableBackedInterviewerEmailChanged,
  };

  // Gate on the current toggle state — if email is disabled, nothing is sent
  // to that recipient, so the dialog/backend should not claim a resend.
  // Toggling itself shows up as a snapshot diff, which is why this gate is
  // applied AFTER the change detection above.
  if (!current.candidate_email_enabled) {
    flags.resend_email_to_candidate = false;
  }
  if (!current.interviewer_email_enabled) {
    flags.resend_email_to_interviewer = false;
  }

  const anyInvite =
    flags.update_calendar_invite_to_candidate ||
    flags.update_calendar_invite_to_interviewer;
  const anyEmail =
    flags.resend_email_to_candidate || flags.resend_email_to_interviewer;

  let variant: SaveConfirmationVariant = null;
  if (anyInvite && anyEmail) variant = 'invites-and-emails';
  else if (anyInvite) variant = 'invites';
  else if (anyEmail) variant = 'emails';

  return { flags, variant };
}
